import type { FC } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import useMuiTheme from '@mui/styles/useTheme';
import { useTheme } from 'next-themes';
import { Highlight } from 'prism-react-renderer';
import { CopyIcon } from '@epic-ui/icons';

import type { ExtendedTheme } from '@theme/theme';
import { useTranslationResponse } from '@isomorphic/hooks/use-translation-response';
import { LIGHT_THEME_KEY } from '@constants/theme';
import { StyledCodeSnippet } from './CodeSnippet.styles';
import { codeSnippetLightTheme, codeSnippetDarkTheme } from './CodeSnippetTheme';

type Language =
  | 'markup'
  | 'bash'
  | 'clike'
  | 'c'
  | 'cpp'
  | 'css'
  | 'javascript'
  | 'jsx'
  | 'coffeescript'
  | 'actionscript'
  | 'css-extr'
  | 'diff'
  | 'git'
  | 'go'
  | 'graphql'
  | 'handlebars'
  | 'json'
  | 'less'
  | 'makefile'
  | 'markdown'
  | 'objectivec'
  | 'ocaml'
  | 'python'
  | 'reason'
  | 'sass'
  | 'scss'
  | 'sql'
  | 'stylus'
  | 'tsx'
  | 'typescript'
  | 'wasm'
  | 'yaml';

interface CodeBlockProps {
  className: string;
  children: string;
}

const getSnippetsLanguage = (language) => {
  switch (language) {
    case 'json':
      return 'json';
    case 'jsx':
      return 'babel';
    case 'js':
      return 'babel';
    case 'markup':
      return 'html';
    case 'css':
      return 'css';
    case 'scss':
      return 'scss';
    case 'less':
      return 'less';

    default:
      return 'babel';
  }
};

export const CodeSnippet: FC<CodeBlockProps> = ({ children, className }) => {
  const { resolvedTheme } = useTheme();
  const epicTheme = useMuiTheme<ExtendedTheme>();
  const codeBlockTheme =
    resolvedTheme === LIGHT_THEME_KEY ? codeSnippetLightTheme(epicTheme) : codeSnippetDarkTheme(epicTheme);
  const copiedTranslate = useTranslationResponse('CopiedToClipBoard');
  const copyTranslate = useTranslationResponse('CodeSnippetCopy');
  const language = useMemo(() => (className?.replace(/language-/, '') || 'tsx') as Language, [className]);

  const [snippetCode, setSnippetCode] = useState<string>('');
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [copiedText, setCopiedText] = useState<string>(copyTranslate);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const codeSnippets = children.trim();

    const getDeps = async (codeSnippets: string) => {
      try {
        const prettier = (await import('prettier/standalone')).default;
        const parserBabel = (await import('prettier/parser-babel')).default;
        const parserHtml = (await import('prettier/parser-html')).default;
        const parserMarkdown = (await import('prettier/parser-markdown')).default;
        const parserCss = (await import('prettier/parser-postcss')).default;

        const formattedCodeSnippet = await prettier.format(codeSnippets, {
          bracketSpacing: true,
          singleQuote: true,
          semi: true,
          endOfLine: 'auto',
          parser: getSnippetsLanguage(language),
          plugins: [parserBabel, parserHtml, parserMarkdown, parserCss],
        });

        setSnippetCode(formattedCodeSnippet);
      } catch (e) {
        setSnippetCode(codeSnippets);
      }
    };

    getDeps(codeSnippets);
  }, [children]);

  useEffect(() => {
    const text = isCopied ? copiedTranslate : copyTranslate;
    setCopiedText(text);
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isCopied, copiedTranslate, copyTranslate]);

  function copyTextToClipboard(text) {
    if (!navigator.clipboard) {
      return;
    }
    navigator.clipboard.writeText(text);
  }

  const copyBtnHandler = () => {
    if (!isCopied) {
      if (timer) {
        clearTimeout(timer);
        setTimer(null);
      } else {
        copyTextToClipboard(snippetCode);
        const time = setTimeout(() => setIsCopied(false), 1000);
        setTimer(time);
        setIsCopied((prevIsCopied) => !prevIsCopied);
      }
    }
  };

  /* eslint-disable react/no-array-index-key */

  return (
    <StyledCodeSnippet className="code_snippet">
      <Highlight code={snippetCode} theme={codeBlockTheme} language="tsx">
        {({ className, tokens, getLineProps, getTokenProps }) => (
          <pre className={className}>
            <div className="code-wrap">
              {tokens.map((line, i) => (
                <div key={i} {...getLineProps({ line })}>
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token })} />
                  ))}
                </div>
              ))}
            </div>
          </pre>
        )}
      </Highlight>
      <Tooltip title={copiedText}>
        <IconButton onClick={copyBtnHandler} size="small">
          <CopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </StyledCodeSnippet>
  );
};
